import { memo, type FC } from 'react';

import { Button } from '@packages/shared/src/ui/Button/Button';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { SimpleFilterValueGridItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/SimpleFilterValueGridItemFragmentFragmentDoc';

/* GraphQL */ `
  fragment SimpleFilterValueGridItemFragment on SearchFilterValueBase {
    id
    name
    count
  }
`;

export type SimpleFilterValueGridItemProps = {
  maskedData: FragmentType<typeof SimpleFilterValueGridItemFragmentFragmentDoc>;
  /** If `true`, the tile is displayed differently to highlight the selection state */
  isSelected?: boolean;
  /** Callback fired when this item is clicked */
  onClick?: (valueId: string) => void;
};

/**
 * A single grid tile to display an individual filter value in a grid
 * */
export const SimpleFilterValueGridItem: FC<SimpleFilterValueGridItemProps> = memo(
  ({ maskedData, isSelected, onClick }) => {
    const { id, name, count } =
      unmask<typeof SimpleFilterValueGridItemFragmentFragmentDoc>(maskedData);

    return (
      <li>
        <Button
          onClick={() => onClick?.(id)}
          className={`border-color-grey-dark flex aspect-square w-full cursor-pointer flex-col items-center justify-center gap-0 rounded-sm bg-transparent p-2 ${isSelected ? 'border-color-common-black border-[3px]' : 'border-[1px]'} transition-[border-color] duration-[250ms] ease-in-out hover:bg-transparent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgba(0,95,204)] focus-visible:not-active:bg-transparent [&:not(:disabled)]:hover:bg-transparent`}
        >
          <h5 className="text-size-headline-base break-normal">{name}</h5>
          <p className="text-size-body-sm text-color-text-darktransparent break-normal">{`(${count})`}</p>
        </Button>
      </li>
    );
  },
);

SimpleFilterValueGridItem.displayName = 'SimpleFilterValueGridItem';
